import { Button, Popover, Typography } from 'antd'
import dayjs from 'dayjs'
import { RolodexDocumentEntry } from 'features/files'
import React from 'react'
import styled from 'styled-components'

import { getFileTypeIcon } from './../utils/DocumentUtils'

const { Text } = Typography

const DocumentContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

const DocumentInfo = styled.div`
  flex: 1;
  cursor: pointer;
`

const DocumentActions = styled.div`
  display: flex;
  align-items: center;
  margin-left: 12px;
  flex-shrink: 0;
`

const DocumentItem = styled.div<{ selected?: boolean }>`
  padding: 10px 12px;
  border-radius: 6px;
  margin-bottom: 8px;
  cursor: pointer;
  background: ${({ selected, theme }) =>
    selected ? theme.forecast.cardGroupBackground : 'transparent'};
  border: 1px solid
    ${({ selected, theme }) =>
      selected ? theme.forecast.cardDividerBackground : 'transparent'};
  transition:
    background 0.2s,
    border 0.2s;
  &:hover {
    background: ${({ theme }) => theme.forecast.cardGroupBackground};
    border-color: ${({ theme }) => theme.forecast.cardDividerBackground};
  }
`

const DocumentHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 4px;
`

const DeleteButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border-radius: 50%;
  background: transparent;
  border-color: transparent;
  color: ${({ theme }) => theme.forecast.defaultText};
  cursor: pointer;
  font-weight: 600;
  transition: color 0.2s ease;

  &:hover,
  &:focus {
    color: ${({ theme }) => theme.forecast.primary};
    background: transparent;
    border-color: transparent;
  }
`

const MB_IN_BYTES = 1000000

const formatFileSize = (size?: number): string => {
  if (!size) return 'Size not available'
  return `${(size / MB_IN_BYTES).toFixed(2)} MB`
}

const formatUploadDate = (uploadDate?: string): string => {
  if (!uploadDate) return 'unknown'
  return dayjs(uploadDate).format('MMM D, YYYY')
}

export interface DocumentListItemProps {
  document: RolodexDocumentEntry
  selected: boolean
  onSelect: (documentId: string) => void
  size?: 'large' | 'small'
  onDelete?: (documentId: string) => Promise<void>
  after?: React.ReactNode
}

export const DocumentListItem: React.FC<DocumentListItemProps> = ({
  document,
  selected,
  onSelect,
  size = 'small',
  onDelete,
  after,
}: DocumentListItemProps) => {
  return (
    <DocumentItem selected={selected}>
      <DocumentContent>
        <DocumentInfo onClick={() => onSelect(document.entry_id)}>
          <Popover
            open={size === 'large' ? false : undefined}
            content={<DocumentMetaSection document={document} />}>
            <DocumentHeader>
              {getFileTypeIcon(document)}
              <Text strong style={{ marginLeft: 8 }}>
                {document.filename || document.visible_name || 'Unknown file'}
              </Text>
            </DocumentHeader>
          </Popover>
          {size === 'large' && <DocumentMetaSection document={document} />}
        </DocumentInfo>
        {onDelete && (
          <DocumentActions>
            <DeleteButton
              shape='circle'
              onClick={(event) => {
                event.stopPropagation()
                event.preventDefault()
                void onDelete?.(document.entry_id)
              }}>
              X
            </DeleteButton>
          </DocumentActions>
        )}
      </DocumentContent>
      {after && after}
    </DocumentItem>
  )
}

const DocumentMetaSection: React.FC<{ document: RolodexDocumentEntry }> = ({
  document: { upload_date, size },
}) => {
  return (
    <>
      <Text type='secondary' style={{ fontSize: 12, marginLeft: 24 }}>
        {formatFileSize(size)}
      </Text>
      <Text type='secondary' style={{ fontSize: 12, marginLeft: 24 }}>
        Created: {formatUploadDate(upload_date)}
      </Text>
    </>
  )
}
